import { Link } from 'react-router-dom'
import { LoonScene } from '../components/LoonScene'
import { Waterline } from '../components/Waterline'

export function Home() {
  return (
    <>
      <section className="hero">
        <div className="wrap hero-grid">
          <div>
            <p className="eyebrow">Loonworks Software · Minnesota</p>
            <h1>A website that runs your business, not just describes it.</h1>
            <p className="lede">
              Loonworks is a Minnesota software studio for businesses that
              outgrew the template builders. The site your customers see,
              plus the scheduling, orders, and daily workflows behind it.
              Built so you're never locked in.
            </p>
            <div className="button-row">
              <Link to="/contact" className="button button--primary">
                Tell me about your business
              </Link>
              <Link to="/work" className="button button--quiet">
                See the work
              </Link>
            </div>
          </div>
          <LoonScene className="hero-art" />
        </div>
        <Waterline hero />
      </section>

      <section className="section">
        <div className="wrap">
          <p className="eyebrow">What you get</p>
          <h2>The whole thing, built as one piece.</h2>
          <p>
            The work starts with how your business operates: who does what,
            which tools already carry part of the load, where things grind.
            Where an off-the-shelf tool fits, you keep it. Custom software
            goes where your business doesn't match the mold.
          </p>
          <div className="card-grid">
            <div>
              <h3>A site built around your work</h3>
              <p>
                Not a theme with your logo dropped in. Pages, flows, and
                language shaped by how your business actually runs.
              </p>
            </div>
            <div>
              <h3>The tools that run it</h3>
              <p>
                Scheduling, orders, job tracking, the screens you and your
                staff work from: the counter side of the website. The part
                template builders can't do.
              </p>
            </div>
            <div>
              <h3>Nothing locks you in</h3>
              <p>
                Your site runs in its own account on Amazon's cloud,
                separate from every other client. Loonworks manages it for
                you, and if you ever move on, the whole account transfers
                with you: code, data, everything.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section work-band">
        <div className="wrap work-card">
          <div>
            <p className="eyebrow">Recent work</p>
            <h2>FloorTracks</h2>
            <p>
              Original music for gymnastics floor routines, sold online for
              years from an aging Joomla store. Loonworks rebuilt it end to
              end: the full catalog carried over, checkout on the site
              itself, and purchased tracks delivered automatically by secure
              download link. Customers pay, the music arrives, and nobody
              has to be at a desk for any of it.
            </p>
            <p>
              <Link to="/work">Read the case study</Link>
              {' · '}
              <a href="https://floortracks.net" target="_blank" rel="noreferrer">
                floortracks.net
              </a>
            </p>
          </div>
        </div>
      </section>

      <section className="section prose">
        <div className="wrap">
          <p className="eyebrow">The math changed</p>
          <h2>A good-enough website is now a liability.</h2>
          <p>
            Breaking into a small-business website used to take a person's
            time, and small businesses weren't worth it. AI changed that
            math. Automated attackers now probe every site on the internet,
            around the clock, at almost no cost. They don't care whether
            you're a national chain or a bait shop. If your site has a known
            hole, something will find it.
          </p>
          <p>
            And the typical good-enough site is exactly where the holes are:
            an aging website system, a pile of plugins from different
            vendors, a theme nobody has touched since launch. Each part has
            its own published vulnerabilities, and the bots work from that
            list. Everything runs fine right up until the morning it
            doesn't. Then it's your customers' data, your search ranking,
            and your name. If you take payments on it, it's a bigger prize.
          </p>
          <p>
            Loonworks builds small, modern, and current: one set of parts
            from one builder, no plugin pile, running in its own locked-down
            account on Amazon's cloud. Nothing on the internet is risk-free,
            and anyone who says otherwise is selling something. But fewer
            parts means fewer doors, and doors that someone is actually
            watching.
          </p>
          <p>
            Not sure where your current site stands?{' '}
            <Link to="/contact">Ask.</Link> It's a short conversation, and
            it costs nothing to have it.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <p className="eyebrow">How it goes</p>
          <h2>Plain steps, no mystery.</h2>
          <ol className="process">
            <li>
              <div>
                <h3>We talk</h3>
                <p>
                  You describe how the business runs, what tools you already
                  lean on, and where things grind. Plain conversation, no
                  jargon.
                </p>
              </div>
            </li>
            <li>
              <div>
                <h3>You get a plan</h3>
                <p>
                  A written proposal in plain language: what gets built and
                  what it costs. You see the real price before you commit to
                  anything.
                </p>
              </div>
            </li>
            <li>
              <div>
                <h3>You watch it take shape</h3>
                <p>
                  Progress shows up on a live link as it happens. You review
                  the real thing, not mockups.
                </p>
              </div>
            </li>
            <li>
              <div>
                <h3>It stays watched</h3>
                <p>
                  Launch isn't goodbye. Hosting includes keeping the site
                  current and watched, so it never becomes the aging system
                  nobody updates.
                </p>
              </div>
            </li>
            <li>
              <div>
                <h3>You're never stuck</h3>
                <p>
                  If you ever want to leave, the whole site goes with you,
                  account and all. Any developer can pick it up from there.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section className="section cta-band">
        <div className="wrap">
          <h2>Outgrown your website?</h2>
          <Link to="/contact" className="button button--primary">
            Tell me about it
          </Link>
        </div>
      </section>
    </>
  )
}
